/**
 * Maps `ApiClientError.code` values onto user-facing messages.
 *
 * The API returns short, machine-readable error codes
 * (`unprocessable_entity`, `no_active_facility`, …) with a generic
 * `message` field. The SPA prefers branded, human-readable copy that
 * tells the broker WHAT happened and what they can do next.
 *
 * Used by the workbench when `POST /quotes/calculate` raises a
 * facility-related rejection. Other call sites (admin, history) can
 * use the same map for consistency.
 *
 * Note: this is presentation only. The API enforces every rule
 * server-side. See `docs/USER-ENTITY-MODEL.md` §6 for the contract.
 */

import { type ApiClientError, isApiClientError } from './cortexApi';

/** Matches the Toast component's accepted variants. */
export type ResolvedApiErrorTone = 'error' | 'info' | 'success';

export interface ResolvedApiError {
  readonly title: string;
  readonly message: string;
  /** Suggested toast variant. */
  readonly tone: ResolvedApiErrorTone;
}

const MAP: Readonly<Record<string, ResolvedApiError>> = {
  no_active_facility: {
    title: 'No active facility',
    message:
      'Your brokerage has no active Lombard facility agreement. Contact your Lombard account manager before producing quotes.',
    tone: 'error',
  },
  entity_type_not_found: {
    title: 'Unknown entity type',
    message:
      'CORTEX does not recognise that entity type. Pick a different type from the list, or contact a CORTEX admin if you believe this is in error.',
    tone: 'error',
  },
  entity_name_taken: {
    title: 'Name already in use',
    message:
      'Another entity is already registered under that name. Use a different legal name or open the existing record instead.',
    tone: 'error',
  },
  user_email_taken: {
    title: 'Email already in use',
    message:
      'Another user is already registered with that email address. Use a different email or open the existing user record instead.',
    tone: 'error',
  },
  role_entity_type_mismatch: {
    title: 'Wrong role for this company',
    message:
      'That role belongs to a different kind of company than this user. A brokerage user can only hold brokerage roles, and an insurer user only insurer roles.',
    tone: 'error',
  },
  role_sod_conflict: {
    title: 'Conflicting duties',
    message:
      'This role cannot be combined with one the user already holds (segregation of duties). Remove the conflicting role first.',
    tone: 'error',
  },
  role_already_assigned: {
    title: 'Role already assigned',
    message: 'This user already holds that role.',
    tone: 'info',
  },
  role_last_admin: {
    title: 'Cannot remove the last administrator',
    message:
      'Assign AdminSuperUser to another active user before removing it here — the system must always have at least one administrator.',
    tone: 'error',
  },
  facility_date_range_invalid: {
    title: 'Invalid date range',
    message: 'The valid-from date must be strictly before the valid-to date.',
    tone: 'error',
  },
  facility_brokerage_range_invalid: {
    title: 'Invalid brokerage range',
    message: 'Minimum brokerage % must be less than or equal to the maximum brokerage %.',
    tone: 'error',
  },
  facility_no_products: {
    title: 'No product selected',
    message: 'At least one product must be included in the facility scope.',
    tone: 'error',
  },
  facility_no_risk_classes: {
    title: 'No risk classes selected',
    message: 'At least one allowed risk class must be specified.',
    tone: 'error',
  },
  facility_bind_limit_required: {
    title: 'Bind limit required',
    message: 'A bind limit (Limit B) must be set when binding authority is enabled.',
    tone: 'error',
  },
  facility_entity_invalid: {
    title: 'Invalid entity',
    message: 'The selected entity does not exist or is not a brokerage. Select a brokerage entity.',
    tone: 'error',
  },
  bind_authority_exceeded: {
    title: 'Bind limit exceeded',
    message:
      'The gross premium on this quote exceeds your current bind authority limit. Refer the quote to a senior underwriter or request a limit increase.',
    tone: 'error',
  },
  bind_no_authority: {
    title: 'No bind authority',
    message:
      'Your role does not grant bind authority for this product. Refer the quote to someone with binding authority.',
    tone: 'error',
  },
  bind_cannotbind_facility: {
    title: 'Facility does not permit binding',
    message:
      'Your current facility agreement does not include binding authority. Contact your Lombard account manager to update the facility.',
    tone: 'error',
  },
  not_found: {
    title: 'Record not found',
    message:
      'That record was not found. It may have been deleted or moved. Try refreshing the list.',
    tone: 'error',
  },
  conflict: {
    title: 'Conflict',
    message: 'The request conflicts with the current state of the record. Refresh and try again.',
    tone: 'error',
  },
  product_not_in_facility: {
    title: 'Product outside facility scope',
    message:
      'This product is not part of your current Lombard facility. Switch products or speak to underwriting if you believe the facility should include it.',
    tone: 'error',
  },
  risk_class_not_in_facility: {
    title: 'Risk class outside facility scope',
    message:
      'One of the categories on this quote uses a risk class your facility does not cover. Adjust the risk class or refer the quote to a Lombard underwriter.',
    tone: 'error',
  },
  unauthorized: {
    title: 'Session expired',
    message:
      'Your session has expired or has not been authenticated. Sign in again via Cloudflare Access to continue.',
    tone: 'error',
  },
  forbidden: {
    title: 'Permission denied',
    message:
      'Your account does not have permission to perform this action. Contact your administrator if you believe this is in error.',
    tone: 'error',
  },
  unprocessable_entity: {
    title: 'Invalid quote',
    message:
      'The quote payload was not accepted by the rating engine. Review the highlighted fields and retry.',
    tone: 'error',
  },
  rate_limited: {
    title: 'Slow down',
    message: 'You have made too many requests in a short time. Wait a moment and retry.',
    tone: 'info',
  },
  internal_error: {
    title: 'Server error',
    message:
      'Something went wrong on our side. The error has been logged — please retry, and contact support if it persists.',
    tone: 'error',
  },
  referral_duplicate_open: {
    title: 'Referral already open',
    message:
      'A referral for this quote is already waiting for review. Check the Referrals queue for its status.',
    tone: 'info',
  },
  referral_not_found: {
    title: 'Referral not found',
    message:
      'This referral record could not be found. It may have been deleted or the link is incorrect.',
    tone: 'error',
  },
  referral_already_resolved: {
    title: 'Referral already resolved',
    message: 'This referral has already been approved or declined and cannot be acted on again.',
    tone: 'info',
  },
  network_error: {
    title: 'Network unreachable',
    message: 'CORTEX cannot reach the API right now. Check your connection and retry.',
    tone: 'error',
  },
  malformed_response: {
    title: 'Unexpected response',
    message:
      'CORTEX received an unexpected response from the API. Retry, and contact support if it persists.',
    tone: 'error',
  },
};

const FALLBACK: ResolvedApiError = {
  title: 'Something went wrong',
  message:
    'CORTEX could not complete that action. Please retry, and contact support if it persists.',
  tone: 'error',
};

/**
 * Resolve a user-facing message from an `ApiClientError` or arbitrary
 * thrown value. Non-API errors fall through to the generic fallback —
 * never leak an internal stack-trace or exception message.
 */
export const resolveApiError = (err: unknown): ResolvedApiError => {
  if (!isApiClientError(err)) return FALLBACK;
  return MAP[err.code] ?? FALLBACK;
};

/**
 * Re-export so consumers can `import { resolveApiError, isApiClientError }`
 * without pulling in the entire cortexApi surface.
 */
export type { ApiClientError };
export { isApiClientError };
